// Monthly Newsletter client API (migs 144 + 145).
//
// The newsletter engine is a public, web-only "White Creek Farm <Month> Review"
// archive. Every read/write routes through the SECURITY DEFINER RPCs from
// supabase-migrations/144_newsletter_engine.sql — this module never touches the
// newsletter_* tables directly (deny-all RLS).
//
// Access split:
//   - PUBLIC (anon-reachable) reads: list_published_newsletters,
//     get_published_newsletter(slug), get_newsletter_preview(slug, token).
//     These are the ONLY three anon RPCs and they return sanitized payloads
//     (approved photo paths only; never source_private_path).
//   - ADMIN (authenticated, role=admin enforced server-side) RPCs: issue
//     create/list/get/save/intake, fact include/manual, photo register/update/
//     approve/cover/remove, publish/unpublish, preview-token rotate, settings.
//
// Photo privacy flow (mig 145): a photo's bytes always land in the PRIVATE
// newsletter-staging bucket first (a new upload, or a re-upload of an existing
// private planner photo). Only on admin approval are the bytes COPIED into the
// PUBLIC newsletter-public bucket at the same relative path, and only then is
// set_newsletter_photo_approved(true) called. Unapprove/remove deletes the
// public bytes. A photo row existing is not public consent — approval is.

import {compressImage} from './photoCompress.js';
import {isStorageDuplicateError} from './tasks.js';
import type {SupabaseClient} from './supabaseTypes.js';

// ── Payload shapes ───────────────────────────────────────────────────────────
//
// Every read below returns a jsonb payload assembled by a SECURITY DEFINER RPC
// in supabase-migrations/144_newsletter_engine.sql (and its successors). The
// AUTHORITY for those column sets is the SQL, and this module is a transport
// wrapper: apart from `ok` / `aiConfigured` on the Edge probe, it reads no
// field of a returned payload. Naming a column list here would therefore be a
// schema invented at the wrong layer — one that nothing checks against the
// function that actually produces it, and that would go stale silently the
// first time a migration adds a key.
//
// So a payload is named as what it provably is: a jsonb object. Callers index
// it and prove the field they need, which is the same obligation they have
// today, stated instead of implied.
export type NewsletterJson = Record<string, unknown>;

// Only READ wrappers take a signal. Mutations deliberately do not: a cancelled
// write is not a cancelled write. `then` is required so this constraint remains
// usable with the plain object fakes in newsletterApi.test.ts.
type MaybeAbortable<Builder> = {then: unknown; abortSignal?: (signal: AbortSignal) => Builder};

const withReadSignal = <Builder extends MaybeAbortable<Builder>>(builder: Builder, signal?: AbortSignal): Builder =>
  signal && builder && typeof builder.abortSignal === 'function' ? builder.abortSignal(signal) : builder;

/** What a read wrapper accepts beyond its own arguments. */
export type NewsletterReadOptions = {signal?: AbortSignal};

/** The identifying fields this module reads off a photo row it is handed. */
export type NewsletterPhotoRef = {
  id: string;
  storagePath: string;
};

/** What probeNewsletterAi resolves with. Non-throwing by contract. */
export type NewsletterAiProbe = {
  ok: boolean;
  aiConfigured: boolean;
};

/** The two server-side passes the newsletter-harvest Edge Function accepts. */
export type NewsletterHarvestStep = 'harvest' | 'draft';

/**
 * The Steer controls updateNewsletterSettings may write. EVERY field is
 * optional because the RPC treats an omitted (null) argument as "preserve" —
 * that partial-update contract is the whole reason the caller can send only
 * `{tonePreset}` without clobbering settings edited elsewhere.
 *
 * `tone` and `voiceExample` additionally accept `''`, which the RPC reads as an
 * explicit clear; that is why they are plain strings rather than non-empty ones.
 */
export type NewsletterSettingsPatch = {
  aiProvider?: string | null;
  aiModel?: string | null;
  tone?: string | null;
  tonePreset?: string | null;
  lengthDetail?: string | null;
  photoMin?: number;
  photoTarget?: number;
  pastIssueContextCount?: number;
  taskAssignee?: string | null;
  draftGenDay?: number | string | null;
  publishTargetDay?: number | string | null;
  voiceExample?: string | null;
};

// A non-null, non-array object. Edge/RPC payloads arrive as `any` from
// supabase-js (the client carries no generated database types), so the two
// places this module reads a field go through one narrowing rather than
// trusting that `any`.
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ── Buckets + storage paths ──────────────────────────────────────────────────

export const NEWSLETTER_STAGING_BUCKET = 'newsletter-staging';
export const NEWSLETTER_PUBLIC_BUCKET = 'newsletter-public';

// register_newsletter_photo enforces that every storage_path lives under
// `newsletter/<issueId>/` with no traversal. Staging and public buckets use the
// SAME relative path, so approval is a same-path byte copy.
export function buildNewsletterStoragePath(issueId: string, token: string, ext: string = 'jpg'): string {
  const safeExt = /^[a-z0-9]{1,5}$/i.test(ext) ? ext.toLowerCase() : 'jpg';
  return `newsletter/${issueId}/${token}.${safeExt}`;
}

// A client-side unique token for the storage filename. register_newsletter_photo
// assigns its own row id (nlp-...) server-side; the storage path only needs to be
// unique per issue (UNIQUE(issue_id, storage_path)).
export function generateNewsletterPhotoToken(): string {
  return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export const MAX_NEWSLETTER_PHOTOS_PER_ISSUE = 12;

// ── Public path helpers (used by the public app + admin preview links) ───────

export const NEWSLETTER_ARCHIVE_PATH = '/newsletter';
export const NEWSLETTER_LATEST_PATH = '/newsletter/latest';

export function buildNewsletterIssuePath(slug: string): string {
  return `/newsletter/${encodeURIComponent(slug)}`;
}

export function buildNewsletterPreviewPath(slug: string, token: string): string {
  return `/newsletter/${encodeURIComponent(slug)}?preview=${encodeURIComponent(token)}`;
}

// ── Ids / display ────────────────────────────────────────────────────────────

// YYYY-MM for a Date (defaults to "now" in the farm's local clock). Used to seed
// the admin "create this month's issue" affordance; the server re-validates.
export function currentYearMonth(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Human label for a YYYY-MM slug, e.g. "June 2026". Pure string math (no Date
// parsing) so it never drifts across time zones.
export function formatYearMonth(yearMonth: unknown): string {
  if (typeof yearMonth !== 'string') return '';
  const m = yearMonth.match(/^(\d{4})-(\d{2})$/);
  if (!m) return yearMonth;
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return yearMonth;
  return `${MONTHS[monthIdx]} ${m[1]}`;
}

// ── Anon (public) reads — the only three anon-reachable RPCs ─────────────────

// The archive is gated by a rotating access key (mig 153). The RPC returns NULL
// when the key is missing/invalid/expired (LOCKED) and [] when the key is valid
// but nothing is published yet — so callers can tell "locked" from "empty".
export async function listPublishedNewsletters(
  sb: SupabaseClient,
  key?: string | null,
): Promise<NewsletterJson[] | null> {
  const {data, error} = await sb.rpc('list_published_newsletters', {p_key: key ?? null});
  if (error) throw new Error(`listPublishedNewsletters: ${error.message || String(error)}`);
  if (data == null) return null; // locked
  return Array.isArray(data) ? data : [];
}

export async function getPublishedNewsletter(
  sb: SupabaseClient,
  slug: string,
  key?: string | null,
): Promise<NewsletterJson | null> {
  const {data, error} = await sb.rpc('get_published_newsletter', {p_slug: slug, p_key: key ?? null});
  if (error) throw new Error(`getPublishedNewsletter: ${error.message || String(error)}`);
  return data || null;
}

export async function getNewsletterPreview(
  sb: SupabaseClient,
  slug: string,
  token: string,
): Promise<NewsletterJson | null> {
  const {data, error} = await sb.rpc('get_newsletter_preview', {p_slug: slug, p_token: token});
  if (error) throw new Error(`getNewsletterPreview: ${error.message || String(error)}`);
  return data || null;
}

// Public URL for an approved newsletter photo (newsletter-public bucket).
//
// `storagePath` is `unknown` because callers read it off a jsonb photo payload,
// where nothing has proved it is a string. The existing empty-path guard is
// widened to cover that: a value that is not a usable path yields '', exactly
// as a missing one always did, instead of handing a non-string to the storage
// client.
export function newsletterPublicPhotoUrl(sb: SupabaseClient, storagePath: unknown): string {
  if (!storagePath || typeof storagePath !== 'string') return '';
  const {data} = sb.storage.from(NEWSLETTER_PUBLIC_BUCKET).getPublicUrl(storagePath);
  return (data && data.publicUrl) || '';
}

// ── Admin RPC wrappers ───────────────────────────────────────────────────────

export async function listNewsletterIssuesAdmin(
  sb: SupabaseClient,
  {signal}: NewsletterReadOptions = {},
): Promise<NewsletterJson[]> {
  const {data, error} = await withReadSignal(sb.rpc('list_newsletter_issues_admin'), signal);
  if (error) throw new Error(`listNewsletterIssuesAdmin: ${error.message || String(error)}`);
  return Array.isArray(data) ? data : [];
}

export async function getNewsletterIssueAdmin(
  sb: SupabaseClient,
  id: string,
  {signal}: NewsletterReadOptions = {},
): Promise<NewsletterJson | null> {
  const {data, error} = await withReadSignal(sb.rpc('get_newsletter_issue_admin', {p_id: id}), signal);
  if (error) throw new Error(`getNewsletterIssueAdmin: ${error.message || String(error)}`);
  return data || null;
}

export async function createNewsletterIssue(
  sb: SupabaseClient,
  yearMonth: string,
  title: string | null = null,
): Promise<unknown> {
  const {data, error} = await sb.rpc('create_newsletter_issue', {
    p_year_month: yearMonth,
    p_title: title || null,
  });
  if (error) throw new Error(`createNewsletterIssue: ${error.message || String(error)}`);
  return data;
}

export async function saveNewsletterDraft(sb: SupabaseClient, id: string, draftPayload: unknown): Promise<unknown> {
  const {data, error} = await sb.rpc('save_newsletter_draft', {p_id: id, p_draft_payload: draftPayload});
  if (error) throw new Error(`saveNewsletterDraft: ${error.message || String(error)}`);
  return data;
}

export async function saveNewsletterIntake(sb: SupabaseClient, id: string, intakeAnswers: unknown): Promise<unknown> {
  const {data, error} = await sb.rpc('save_newsletter_intake', {p_id: id, p_intake_answers: intakeAnswers});
  if (error) throw new Error(`saveNewsletterIntake: ${error.message || String(error)}`);
  return data;
}

export async function setNewsletterFactIncluded(
  sb: SupabaseClient,
  factId: string,
  included: boolean,
): Promise<unknown> {
  const {data, error} = await sb.rpc('set_newsletter_fact_included', {p_fact_id: factId, p_included: !!included});
  if (error) throw new Error(`setNewsletterFactIncluded: ${error.message || String(error)}`);
  return data;
}

export async function addNewsletterManualFact(
  sb: SupabaseClient,
  {
    issueId,
    title,
    summary = null,
    program = 'manual',
  }: {issueId: string; title: string; summary?: string | null; program?: string},
): Promise<unknown> {
  const {data, error} = await sb.rpc('add_newsletter_manual_fact', {
    p_issue_id: issueId,
    p_title: title,
    p_summary: summary || null,
    p_program: program || 'manual',
  });
  if (error) throw new Error(`addNewsletterManualFact: ${error.message || String(error)}`);
  return data;
}

export async function registerNewsletterPhoto(
  sb: SupabaseClient,
  {
    issueId,
    storagePath,
    sourcePrivatePath = null,
    caption = null,
    altText = null,
    firstName = null,
  }: {
    issueId: string;
    storagePath: string;
    sourcePrivatePath?: string | null;
    caption?: string | null;
    altText?: string | null;
    firstName?: string | null;
  },
): Promise<unknown> {
  const {data, error} = await sb.rpc('register_newsletter_photo', {
    p_issue_id: issueId,
    p_storage_path: storagePath,
    p_source_private_path: sourcePrivatePath || null,
    p_caption: caption || null,
    p_alt_text: altText || null,
    p_first_name: firstName || null,
  });
  if (error) throw new Error(`registerNewsletterPhoto: ${error.message || String(error)}`);
  return data;
}

export async function updateNewsletterPhoto(
  sb: SupabaseClient,
  {
    id,
    caption,
    altText,
    firstName,
    sortOrder,
  }: {
    id: string;
    caption?: string | null;
    altText?: string | null;
    firstName?: string | null;
    sortOrder?: number | null;
  },
): Promise<unknown> {
  const {data, error} = await sb.rpc('update_newsletter_photo', {
    p_id: id,
    p_caption: caption ?? null,
    p_alt_text: altText ?? null,
    p_first_name: firstName ?? null,
    p_sort_order: sortOrder ?? null,
  });
  if (error) throw new Error(`updateNewsletterPhoto: ${error.message || String(error)}`);
  return data;
}

export async function setNewsletterCover(sb: SupabaseClient, issueId: string, photoId: string): Promise<unknown> {
  const {data, error} = await sb.rpc('set_newsletter_cover', {p_issue_id: issueId, p_photo_id: photoId});
  if (error) throw new Error(`setNewsletterCover: ${error.message || String(error)}`);
  return data;
}

export async function publishNewsletterIssue(sb: SupabaseClient, id: string): Promise<unknown> {
  const {data, error} = await sb.rpc('publish_newsletter_issue', {p_id: id});
  if (error) throw new Error(`publishNewsletterIssue: ${error.message || String(error)}`);
  return data;
}

export async function unpublishNewsletterIssue(sb: SupabaseClient, id: string): Promise<unknown> {
  const {data, error} = await sb.rpc('unpublish_newsletter_issue', {p_id: id});
  if (error) throw new Error(`unpublishNewsletterIssue: ${error.message || String(error)}`);
  return data;
}

export async function regenerateNewsletterPreviewToken(sb: SupabaseClient, id: string): Promise<unknown> {
  const {data, error} = await sb.rpc('regenerate_newsletter_preview_token', {p_id: id});
  if (error) throw new Error(`regenerateNewsletterPreviewToken: ${error.message || String(error)}`);
  return data;
}

// ── Automation (mig 146 + newsletter-harvest Edge Function) ──────────────────

// Trigger the server-side harvest / AI-draft for an issue. The Edge Function
// authenticates the admin (rpc is_admin on the caller JWT), runs the requested
// steps with the service role, and returns a summary. The AI provider key never
// leaves the function; this only passes the issue id + which steps to run.
//   steps: ['harvest'] | ['draft'] | ['harvest','draft']
//   overwrite: when running 'draft', replace existing draft blocks (default true)
export async function runNewsletterHarvest(
  sb: SupabaseClient,
  {
    issueId,
    steps = ['harvest', 'draft'],
    overwrite = true,
    revisionNotes = '',
  }: {issueId: string; steps?: NewsletterHarvestStep[]; overwrite?: boolean; revisionNotes?: string},
): Promise<NewsletterJson> {
  const {data, error} = await sb.functions.invoke('newsletter-harvest', {
    body: {mode: 'admin', issueId, steps, overwrite, revisionNotes},
  });
  if (error) {
    // Edge errors carry the HTTP body on error.context in supabase-js v2.
    let detail = error.message || String(error);
    try {
      const body = error.context && (await error.context.json());
      if (body && body.error) detail = body.error;
    } catch (_e) {
      /* keep the generic message */
    }
    throw new Error(`runNewsletterHarvest: ${detail}`);
  }
  if (data && data.ok === false) throw new Error(`runNewsletterHarvest: ${data.error || 'failed'}`);
  return data || {};
}

// One-action "Prepare issue": harvest planner facts THEN generate the draft in a
// single server pass — the autopilot primary action. overwrite replaces existing
// draft blocks (the admin Prepare button passes true; the monthly cron uses false
// so it never clobbers admin edits).
export async function prepareNewsletterIssue(
  sb: SupabaseClient,
  {issueId, overwrite = true}: {issueId: string; overwrite?: boolean},
): Promise<NewsletterJson> {
  return runNewsletterHarvest(sb, {issueId, steps: ['harvest', 'draft'], overwrite});
}

// Gather facts ONLY (harvest, no AI draft) — the "direction first" entry point.
// Scan the planner for this month's facts so the admin can curate them and add
// direction (Q&A, tone) BEFORE the AI writes anything.
export async function gatherNewsletterFacts(sb: SupabaseClient, {issueId}: {issueId: string}): Promise<NewsletterJson> {
  return runNewsletterHarvest(sb, {issueId, steps: ['harvest']});
}

// Regenerate the draft only (no re-harvest), optionally with revision notes the
// AI applies in place to the current draft ("warmer tone", "shorten the cattle
// section"). Revision notes only affect the Anthropic provider; the offline
// template composer regenerates deterministically and ignores them.
export async function regenerateNewsletterDraft(
  sb: SupabaseClient,
  {issueId, revisionNotes = ''}: {issueId: string; revisionNotes?: string},
): Promise<NewsletterJson> {
  return runNewsletterHarvest(sb, {issueId, steps: ['draft'], overwrite: true, revisionNotes});
}

// Fulfill (or clear) one photo-plan slot with an approved photo. photoId null
// clears the slot.
export async function setNewsletterPhotoPlanSlot(
  sb: SupabaseClient,
  {issueId, slotId, photoId = null}: {issueId: string; slotId: string; photoId?: string | null},
): Promise<unknown> {
  const {data, error} = await sb.rpc('set_newsletter_photo_plan_slot', {
    p_issue_id: issueId,
    p_slot_id: slotId,
    p_photo_id: photoId ?? null,
  });
  if (error) throw new Error(`setNewsletterPhotoPlanSlot: ${error.message || String(error)}`);
  return data;
}

// Ask the Edge Function whether the AI provider key is configured (boolean only;
// the key never leaves the function). Non-throwing — the settings UI treats a
// failure / pre-deploy function as "unknown" and falls back to the template.
export async function probeNewsletterAi(sb: SupabaseClient): Promise<NewsletterAiProbe> {
  try {
    const {data, error} = await sb.functions.invoke('newsletter-harvest', {body: {mode: 'admin', probe: true}});
    if (error) return {ok: false, aiConfigured: false};
    return {ok: !!(data && data.ok), aiConfigured: !!(data && data.aiConfigured)};
  } catch (_e) {
    return {ok: false, aiConfigured: false};
  }
}

export async function listNewsletterRunsAdmin(
  sb: SupabaseClient,
  issueId: string,
  {signal}: NewsletterReadOptions = {},
): Promise<NewsletterJson[]> {
  const {data, error} = await withReadSignal(sb.rpc('list_newsletter_runs_admin', {p_issue_id: issueId}), signal);
  if (error) throw new Error(`listNewsletterRunsAdmin: ${error.message || String(error)}`);
  return Array.isArray(data) ? data : [];
}

export async function getNewsletterSettings(
  sb: SupabaseClient,
  {signal}: NewsletterReadOptions = {},
): Promise<NewsletterJson> {
  const {data, error} = await withReadSignal(sb.rpc('get_newsletter_settings'), signal);
  if (error) throw new Error(`getNewsletterSettings: ${error.message || String(error)}`);
  return data || {};
}

// Admin: mint a fresh public archive access link (mig 153). Instantly revokes the
// prior link; the new key is valid for `days` (server clamps 1..60, default 7).
// Returns the updated settings (including the new archiveAccessToken + expiry).
export async function regenerateNewsletterArchiveLink(sb: SupabaseClient, days?: number): Promise<NewsletterJson> {
  const {data, error} = await sb.rpc('regenerate_newsletter_archive_link', {
    p_days: Number.isFinite(days) ? days : null,
  });
  if (error) throw new Error(`regenerateNewsletterArchiveLink: ${error.message || String(error)}`);
  return data || {};
}

// Build the shareable public archive URL for a given key (origin-aware).
export function buildNewsletterArchiveLink(key?: string | null): string {
  if (!key) return '';
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}/newsletter?key=${encodeURIComponent(key)}`;
}

// Append the gated archive key to an internal newsletter path so navigation
// keeps the link working (archive -> issue -> back all carry ?key=).
export function withNewsletterKey(path: string, key?: string | null): string {
  if (!key) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}key=${encodeURIComponent(key)}`;
}

// Update newsletter settings. Every field is a PARTIAL update: an omitted key
// (undefined) maps to null, which the RPC reads as "preserve current value", so
// the Steer UI can send only the controls it owns (e.g. {tonePreset}) without
// clobbering settings edited elsewhere. `tone` and `voiceExample` additionally
// support explicit clearing: pass an empty string '' to clear the value to NULL
// (the RPC distinguishes ''=clear from null=preserve). The writing example is
// never logged here.
export async function updateNewsletterSettings(
  sb: SupabaseClient,
  {
    aiProvider,
    aiModel,
    tone,
    tonePreset,
    lengthDetail,
    photoMin,
    photoTarget,
    pastIssueContextCount,
    taskAssignee,
    draftGenDay,
    publishTargetDay,
    voiceExample,
  }: NewsletterSettingsPatch = {},
): Promise<unknown> {
  const {data, error} = await sb.rpc('update_newsletter_settings', {
    p_ai_provider: aiProvider ?? null,
    p_ai_model: aiModel ?? null,
    // '' clears the custom tone override (preset then drives the draft); undefined
    // preserves. Only null/undefined coalesce — an explicit '' is passed through.
    p_tone: tone ?? null,
    p_tone_preset: tonePreset ?? null,
    p_length_detail: lengthDetail ?? null,
    p_photo_min: Number.isFinite(photoMin) ? photoMin : null,
    p_photo_target: Number.isFinite(photoTarget) ? photoTarget : null,
    p_past_issue_context_count: Number.isFinite(pastIssueContextCount) ? pastIssueContextCount : null,
    p_task_assignee: taskAssignee ?? null,
    p_draft_gen_day: draftGenDay ?? null,
    p_publish_target_day: publishTargetDay ?? null,
    // '' clears the writing example to NULL; undefined preserves.
    p_voice_example: voiceExample ?? null,
  });
  if (error) throw new Error(`updateNewsletterSettings: ${error.message || String(error)}`);
  return data;
}

// Recent published issues + their included facts — powers the brief's repetition
// warnings (and any "what did we say last month" admin context).
export async function getNewsletterRecentPublishedAdmin(
  sb: SupabaseClient,
  {limit = 3, excludeId = null, signal}: {limit?: number; excludeId?: string | null} & NewsletterReadOptions = {},
): Promise<NewsletterJson[]> {
  const {data, error} = await withReadSignal(
    sb.rpc('get_newsletter_recent_published_admin', {p_limit: limit, p_exclude_id: excludeId}),
    signal,
  );
  if (error) throw new Error(`getNewsletterRecentPublishedAdmin: ${error.message || String(error)}`);
  return Array.isArray(data) ? data : [];
}

// ── Staging storage (private) ────────────────────────────────────────────────

// Upload a (compressed) image blob into the PRIVATE staging bucket and return
// the relative storage path. Idempotent: a duplicate object is treated as
// success (deterministic path + upsert:false, mirroring the task-photo owner).
export async function uploadNewsletterStagingPhoto(
  sb: SupabaseClient,
  issueId: string,
  blob: Blob,
  {token, ext = 'jpg'}: {token?: string; ext?: string} = {},
): Promise<string> {
  const relPath = buildNewsletterStoragePath(issueId, token || generateNewsletterPhotoToken(), ext);
  const compressed = await compressImage(blob);
  const {error} = await sb.storage
    .from(NEWSLETTER_STAGING_BUCKET)
    .upload(relPath, compressed, {contentType: compressed.type || 'image/jpeg', upsert: false});
  if (error && !isStorageDuplicateError(error)) {
    throw new Error(`uploadNewsletterStagingPhoto: ${error.message || String(error)}`);
  }
  return relPath;
}

// Re-upload an EXISTING private planner photo into staging. The caller passes a
// signed URL for the private source; we fetch the bytes and stage them so the
// public copy is a real byte copy, never a hotlinked private signed URL.
export async function stageNewsletterPhotoFromUrl(
  sb: SupabaseClient,
  issueId: string,
  sourceUrl: string,
  {ext = 'jpg'}: {ext?: string} = {},
): Promise<string> {
  const res = await fetch(sourceUrl);
  if (!res.ok) throw new Error(`stageNewsletterPhotoFromUrl: fetch failed (${res.status})`);
  const blob = await res.blob();
  return uploadNewsletterStagingPhoto(sb, issueId, blob, {ext});
}

export async function getNewsletterStagingSignedUrl(
  sb: SupabaseClient,
  storagePath: string,
  ttlSeconds: number = 600,
): Promise<string> {
  // This helper remains intentionally action-time only: approval needs a
  // freshly minted staging URL to copy bytes into the public bucket. Mounted
  // editor thumbnails are owned separately by newsletterStagingUrlData.
  const {data, error} = await sb.storage.from(NEWSLETTER_STAGING_BUCKET).createSignedUrl(storagePath, ttlSeconds);
  if (error) throw new Error(`getNewsletterStagingSignedUrl: ${error.message || String(error)}`);
  return (data && data.signedUrl) || '';
}

// Copy staged bytes into the PUBLIC bucket at the same relative path. Cross-
// bucket copy isn't a single storage call, so we download (signed) and re-upload.
async function copyStagingBytesToPublic(sb: SupabaseClient, storagePath: string): Promise<void> {
  const signed = await getNewsletterStagingSignedUrl(sb, storagePath, 120);
  const res = await fetch(signed);
  if (!res.ok) throw new Error(`copyStagingBytesToPublic: staging fetch failed (${res.status})`);
  const blob = await res.blob();
  const {error} = await sb.storage
    .from(NEWSLETTER_PUBLIC_BUCKET)
    .upload(storagePath, blob, {contentType: blob.type || 'image/jpeg', upsert: true});
  if (error && !isStorageDuplicateError(error)) {
    throw new Error(`copyStagingBytesToPublic: ${error.message || String(error)}`);
  }
}

// Checked bucket-object deletion. Supabase storage reports a MISSING object as a
// non-error (empty data, error=null), so an already-gone object is fine — but a
// real permission/network/storage failure is surfaced (thrown), never swallowed,
// so the admin sees the failed cleanup and can retry. This matters most for the
// PUBLIC bucket: a silently-failed delete would leave approved bytes reachable
// by public URL.
async function removeBucketObject(sb: SupabaseClient, bucket: string, storagePath: string): Promise<void> {
  if (!storagePath) return;
  const {error} = await sb.storage.from(bucket).remove([storagePath]);
  if (error) throw new Error(`remove ${bucket}/${storagePath}: ${error.message || String(error)}`);
}

async function deletePublicBytes(sb: SupabaseClient, storagePath: string): Promise<void> {
  return removeBucketObject(sb, NEWSLETTER_PUBLIC_BUCKET, storagePath);
}

async function deleteStagingBytes(sb: SupabaseClient, storagePath: string): Promise<void> {
  return removeBucketObject(sb, NEWSLETTER_STAGING_BUCKET, storagePath);
}

// ── Approval gate (consent-to-public) ────────────────────────────────────────

// Approve a photo: copy staging bytes -> public FIRST, then flip approved=true
// (the RPC the public read path keys on). If the copy fails we never flip the
// flag, so unapproved bytes are never reachable by public URL.
export async function approveNewsletterPhoto(sb: SupabaseClient, photo: NewsletterPhotoRef): Promise<unknown> {
  await copyStagingBytesToPublic(sb, photo.storagePath);
  const {data, error} = await sb.rpc('set_newsletter_photo_approved', {p_id: photo.id, p_approved: true});
  if (error) throw new Error(`approveNewsletterPhoto: ${error.message || String(error)}`);
  return data;
}

// Unapprove: flip approved=false FIRST (public read path stops serving it), then
// delete the public bytes.
export async function unapproveNewsletterPhoto(sb: SupabaseClient, photo: NewsletterPhotoRef): Promise<unknown> {
  const {data, error} = await sb.rpc('set_newsletter_photo_approved', {p_id: photo.id, p_approved: false});
  if (error) throw new Error(`unapproveNewsletterPhoto: ${error.message || String(error)}`);
  await deletePublicBytes(sb, photo.storagePath);
  return data;
}

// Remove a photo entirely: delete public + staging bytes, then drop the row.
export async function removeNewsletterPhoto(sb: SupabaseClient, photo: NewsletterPhotoRef): Promise<unknown> {
  await deletePublicBytes(sb, photo.storagePath);
  await deleteStagingBytes(sb, photo.storagePath);
  const {data, error} = await sb.rpc('remove_newsletter_photo', {p_id: photo.id});
  if (error) throw new Error(`removeNewsletterPhoto: ${error.message || String(error)}`);
  return data;
}

// ── Error classification ─────────────────────────────────────────────────────
// Deterministic validation failures carry the NEWSLETTER_VALIDATION prefix from
// the RPCs; everything else is transient (network/5xx) and worth a retry.

export function isNewsletterValidationError(err: unknown): boolean {
  return isPlainRecord(err) && typeof err.message === 'string' && err.message.includes('NEWSLETTER_VALIDATION');
}

export function friendlyNewsletterError(err: unknown): string {
  const e = isPlainRecord(err) ? err : null;
  const msg = e && e.message ? String(e.message) : String(err || 'Unknown error');
  const idx = msg.indexOf('NEWSLETTER_VALIDATION:');
  if (idx >= 0) return msg.slice(idx + 'NEWSLETTER_VALIDATION:'.length).trim();
  return msg.replace(/^[a-zA-Z_]+:\s*/, '');
}
